import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  InputAdornment,
  IconButton,
  Button,
  Autocomplete,
  Grid,
  Card,
  CardContent,
  Tooltip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  Collapse,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search,
  RestartAlt,
  AttachFile,
  Description,
  Business,
  FolderOpen,
  FileDownload,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { transactionsApi } from '../services/api';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';

// Display labels for transaction types
const TYPE_LABELS = {
  PurchaseOrder: 'Purchase Order',
  SalesOrder: 'Sales Order',
  Bill: 'Bill',
  Invoice: 'Invoice',
  VendorCredit: 'Vendor Credit',
  BillPaymentCheck: 'Bill Payment (Check)',
  BillPaymentCreditCard: 'Bill Payment (CC)',
  ReceivePayment: 'Receive Payment',
  ItemReceipt: 'Item Receipt',
};

// Short labels for the table
const TYPE_SHORT = {
  PurchaseOrder: 'PO',
  SalesOrder: 'SO',
  Bill: 'Bill',
  Invoice: 'Inv',
  VendorCredit: 'VC',
  BillPaymentCheck: 'BPChk',
  BillPaymentCreditCard: 'BPCC',
  ReceivePayment: 'RcvPmt',
  ItemReceipt: 'IR',
};

// Colors for type chips
const TYPE_COLORS = {
  PurchaseOrder: '#32578b',
  SalesOrder: '#2e7d32',
  Bill: '#ed6c02',
  Invoice: '#9c27b0',
  VendorCredit: '#d32f2f',
  BillPaymentCheck: '#0277bd',
  BillPaymentCreditCard: '#00838f',
  ReceivePayment: '#558b2f',
  ItemReceipt: '#6a1b9a',
};

function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetTrail } = useBreadcrumbs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Search page is the root — reset breadcrumb trail on mount
  useEffect(() => {
    resetTrail();
  }, [resetTrail]);

  // Search state — all persisted in URL so back navigation restores them
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [entity, setEntity] = useState(searchParams.get('entity') || '');
  const [transactionType, setTransactionType] = useState(searchParams.get('type') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [amountMin, setAmountMin] = useState(searchParams.get('amountMin') || '');
  const [amountMax, setAmountMax] = useState(searchParams.get('amountMax') || '');
  const [hideWithoutAttachments, setHideWithoutAttachments] = useState(
    searchParams.get('hideNoAtt') === 'true'  // default false unless explicitly set to true
  );

  // Results state
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '0'));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') || '25'));
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'refNumber');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'asc');
  const [loading, setLoading] = useState(false);

  // Stats and entities
  const [stats, setStats] = useState(null);
  const [entities, setEntities] = useState([]);

  const [searchLineItems, setSearchLineItems] = useState(false);

  // Load stats and entities on mount
  useEffect(() => {
    transactionsApi.getStats().then(setStats).catch(console.error);
    transactionsApi.getEntities().then(setEntities).catch(console.error);
  }, []);

  // Search function
  const performSearch = useCallback(async () => {
    setLoading(true);
    try {
      // When user is actively searching by text, bypass the attachment filter
      const params = {
        search: search || undefined,
        searchLineItems: (search && searchLineItems) ? 'true' : undefined,
        entity: entity || undefined,
        transactionType: transactionType || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        status: status || undefined,
        amountMin: amountMin || undefined,
        amountMax: amountMax || undefined,
        hasAttachments: hideWithoutAttachments ? 'true' : undefined,
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
      };

      const result = await transactionsApi.search(params);
      setResults(result.data);
      setTotal(result.pagination.total);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [search, searchLineItems, entity, transactionType, dateFrom, dateTo, status, amountMin, amountMax, hideWithoutAttachments, page, rowsPerPage, sortBy, sortOrder]);

  // Trigger search on param changes
  useEffect(() => {
    performSearch();
  }, [performSearch]);

  // Update URL params — includes sort/filter state so back navigation restores everything
  useEffect(() => {
    const params = {};
    if (search) params.q = search;
    if (entity) params.entity = entity;
    if (transactionType) params.type = transactionType;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (status) params.status = status;
    if (amountMin) params.amountMin = amountMin;
    if (amountMax) params.amountMax = amountMax;
    if (page > 0) params.page = page.toString();
    if (sortBy !== 'refNumber') params.sortBy = sortBy;
    if (sortOrder !== 'asc') params.sortOrder = sortOrder;
    if (hideWithoutAttachments) params.hideNoAtt = 'true';
    if (rowsPerPage !== 25) params.limit = rowsPerPage.toString();
    setSearchParams(params, { replace: true });
  }, [search, entity, transactionType, dateFrom, dateTo, status, amountMin, amountMax, page, sortBy, sortOrder, hideWithoutAttachments, rowsPerPage, setSearchParams]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const clearFilters = () => {
    setSearch('');
    setEntity('');
    setTransactionType('');
    setDateFrom('');
    setDateTo('');
    setStatus('');
    setAmountMin('');
    setAmountMax('');
    setPage(0);
  };

  const handleExportCsv = async () => {
    const isSearching = search.trim().length > 0;
    const params = {
      search: search || undefined,
      searchLineItems: (search && searchLineItems) ? 'true' : undefined,
      entity: entity || undefined,
      transactionType: transactionType || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      status: status || undefined,
      amountMin: amountMin || undefined,
      amountMax: amountMax || undefined,
      hasAttachments: (!isSearching && hideWithoutAttachments) ? 'true' : undefined,
      sortBy,
      sortOrder,
    };
    try {
      await transactionsApi.exportCsv(params);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  const hasFilters = search || entity || transactionType || dateFrom || dateTo || status || amountMin || amountMax;

  return (
    <div className="page-container">
      <Typography variant="h4" fontWeight="bold" sx={{ color: '#32578b', mb: 1, maxWidth: 1200, width: '100%', display: { xs: 'none', sm: 'block' } }}>
        IMPRES QB Archive
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ maxWidth: 1200, width: '100%', mb: 1, display: { xs: 'none', sm: 'flex' } }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Description sx={{ color: '#32578b', fontSize: 32, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography variant="h5" fontWeight="bold">{stats.totalDocuments?.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">Documents</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <AttachFile sx={{ color: '#32578b', fontSize: 32, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography variant="h5" fontWeight="bold">{stats.totalAttachments?.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">Attachments</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Business sx={{ color: '#32578b', fontSize: 32, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography variant="h5" fontWeight="bold">{stats.uniqueEntities?.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">Vendors / Customers</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <FolderOpen sx={{ color: '#32578b', fontSize: 32, display: { xs: 'none', sm: 'block' }, mx: 'auto' }} />
                <Typography variant="h5" fontWeight="bold">{stats.docsWithAttachments?.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary">Docs with Files</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search & Filters */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, width: '100%', maxWidth: 1200 }}>
        <Box
          onClick={isMobile ? () => setFiltersOpen(!filtersOpen) : undefined}
          sx={{
            mb: filtersOpen ? 2 : 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: isMobile ? 'pointer' : 'default',
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Search sx={{ color: '#32578b' }} />
            Search & Filters
          </Typography>
          {isMobile && (
            <IconButton size="small" sx={{ ml: 1 }}>
              {filtersOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
        <Collapse in={isMobile ? filtersOpen : true}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              placeholder="Search ref #, vendor, customer, memo..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Autocomplete
              freeSolo
              options={entities}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.entityName || ''}
              value={entity}
              onInputChange={(_, newValue) => { setEntity(newValue); setPage(0); }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Vendor / Customer" size="small" />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={transactionType}
                label="Type"
                onChange={(e) => { setTransactionType(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => { setStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
                <MenuItem value="Received">Received</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              type="date"
              label="From"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                if (!e.target.value || (dateTo && dateTo < e.target.value)) setDateTo('');
                setPage(0);
              }}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              type="date"
              label="To"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              size="small"
              InputLabelProps={{ shrink: true }}
              disabled={!dateFrom}
              inputProps={{ min: dateFrom }}
            />
          </Grid>
        </Grid>
        {/* Second row: amount range + action buttons */}
        <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              type="number"
              label="Min $"
              value={amountMin}
              onChange={(e) => { setAmountMin(e.target.value); setPage(0); }}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              type="number"
              label="Max $"
              value={amountMax}
              onChange={(e) => { setAmountMax(e.target.value); setPage(0); }}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {hasFilters && (
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<RestartAlt />}
                  onClick={clearFilters}
                  sx={{ whiteSpace: 'nowrap', flex: { xs: 1, sm: 'none' } }}
                >
                  Reset
                </Button>
              )}
              {total > 0 && (
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<FileDownload />}
                  onClick={handleExportCsv}
                  sx={{ whiteSpace: 'nowrap', flex: { xs: 1, sm: 'none' } }}
                >
                  Export CSV
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ mt: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideWithoutAttachments}
                onChange={(e) => { setHideWithoutAttachments(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Hide documents without attachments
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchLineItems}
                onChange={(e) => { setSearchLineItems(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label={<Typography variant="body2" color="text.secondary">Include line items in search</Typography>}
          />
        </Box>
        </Collapse>
      </Paper>

      {/* Results Table */}
      <Box sx={{ position: 'relative', maxWidth: 1200, width: '100%' }}>
        {/* Gradient scroll hint — fades right edge to indicate horizontal scrollability */}
        <Box sx={{
          display: { xs: 'block', sm: 'none' },
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 48,
          background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))',
          pointerEvents: 'none',
          zIndex: 1,
          borderRadius: '0 4px 4px 0',
        }} />
      <TableContainer component={Paper} sx={{ maxWidth: 1200, width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: '#32578b' }} />
            Transactions
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>
                <TableSortLabel
                  active={sortBy === 'transactionType'}
                  direction={sortBy === 'transactionType' ? sortOrder : 'asc'}
                  onClick={() => handleSort('transactionType')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'refNumber'}
                  direction={sortBy === 'refNumber' ? sortOrder : 'asc'}
                  onClick={() => handleSort('refNumber')}
                >
                  Ref #
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'entityName'}
                  direction={sortBy === 'entityName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('entityName')}
                >
                  Vendor / Customer
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                <TableSortLabel
                  active={sortBy === 'txnDate'}
                  direction={sortBy === 'txnDate' ? sortOrder : 'asc'}
                  onClick={() => handleSort('txnDate')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortBy === 'amount'}
                  direction={sortBy === 'amount' ? sortOrder : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Attachments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {hasFilters ? 'No documents match your filters.' : 'No documents found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              results.map((txn) => (
                <TableRow
                  key={txn.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      window.open(`/transaction/${txn.id}`, '_blank');
                    } else {
                      navigate(`/transaction/${txn.id}`);
                    }
                  }}
                  onAuxClick={(e) => {
                    if (e.button === 1) { e.preventDefault(); window.open(`/transaction/${txn.id}`, '_blank'); }
                  }}
                >
                  <TableCell>
                    <Chip
                      label={TYPE_SHORT[txn.transactionType] || txn.transactionType}
                      size="small"
                      sx={{
                        backgroundColor: TYPE_COLORS[txn.transactionType] || '#757575',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        minWidth: 36,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <RouterLink
                      to={`/transaction/${txn.id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {txn.refNumber}
                      </Typography>
                    </RouterLink>
                  </TableCell>
                  <TableCell>
                    {txn.entityId ? (
                      <RouterLink
                        to={`/entity/${txn.entityId}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: 'none' }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {txn.entityName}
                        </Typography>
                      </RouterLink>
                    ) : (
                      txn.entityName
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    {txn.txnDate ? format(new Date(txn.txnDate), 'MM/dd/yyyy') : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {txn.amount ? `$${parseFloat(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {txn._count?.attachments > 0 ? (
                      <Chip
                        icon={<AttachFile />}
                        label={txn._count.attachments}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">None</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ '& .MuiTablePagination-selectLabel': { display: { xs: 'none', sm: 'block' } } }}
        />
      </TableContainer>
      </Box>{/* end scroll hint wrapper */}
    </div>
  );
}

export default SearchPage;
